'use client';

import classNames from 'classnames';
import Image from 'next/image';
import { useSearchParams } from 'next/navigation';
import { type ComponentProps, type ReactNode, useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import logProPurchaseGetStartedClicked from '@alltrails/amplitude/events/logProPurchaseGetStartedClicked';
import logProPurchasePageViewed from '@alltrails/amplitude/events/logProPurchasePageViewed';
import Typography from '@alltrails/core/components/Typography';
import { useLanguageRegionCode } from '@alltrails/core/hooks/useLanguageRegionCode';
import type PurchaseTier from '@alltrails/core/types/PurchaseTier';
import SubscriptionsNewResponse from '@alltrails/core/types/SubscriptionsNewResponse';
import Button from '@alltrails/denali/components/Button';
import Heading from '@alltrails/denali/components/Heading';
import useIsMobileSizedScreen from '@alltrails/denali/hooks/useIsMobileSizedScreen';
import { BREAKPOINT_SM_SIZE } from '@alltrails/denali/tokens';
import useExposureEvent from '@alltrails/experiments/hooks/useExposureEvent';
import Page from '@alltrails/layout/components/Page';
import getPurchaseScreenTypePlan from '@alltrails/payment/utils/getPurchaseScreenTypePlan';
import type Coupon from '@alltrails/types/Coupon';
import type Plans from '@alltrails/types/Plans';
import type { UserId } from '@alltrails/types/User';
import ContentBlock from '@alltrails/upsell/components/UpsellSystem/ContentBlock/ContentBlock';
import HeadToTheAppDialog from '@alltrails/upsell/components/UpsellSystem/HeadToTheAppDialog';
import getPricingText from '@alltrails/upsell/components/UpsellSystem/PlanSelection/getPricingText';
import PlanSelection from '@alltrails/upsell/components/UpsellSystem/PlanSelection/PlanSelection';
import TrialReminder from '@alltrails/upsell/components/UpsellSystem/TrialReminder/TrialReminder';
import UnableToUpgradeDialog from '@alltrails/upsell/components/UpsellSystem/UnableToUpgradeDialog';
import backgroundSrc from '@/components/MemberDealsPage/member-deals-bg.avif';
import PeakMembershipIncludes from '@/components/MembershipIncludes/PeakMembershipIncludes';
import PlusMembershipIncludes from '@/components/MembershipIncludes/PlusMembershipIncludes';
import getHeadingText from './getHeadingText';
import { getPlanCheckoutUrl, type PromoCampaignQueryValue } from './getPlanCheckoutUrl';
import getPlansLandingPageCtaText from './getPlansLandingPageCtaText';
import styles from './PlansLandingPage.module.scss';

type PlansLandingPagePromoImage = {
  src: ComponentProps<typeof Image>['src'];
  alt: ComponentProps<typeof Image>['alt'];
  width: ComponentProps<typeof Image>['width'];
  height?: ComponentProps<typeof Image>['height'];
};

type PlansLandingPageProps = {
  className?: string;
  coupon?: Coupon;
  headingOverride?: ReactNode;
  isOsanoTopExperimentPage?: boolean;
  isPlansWelcomePlusInfoControlEnabled?: boolean;
  isPeakPromoEligible?: boolean;
  isPlusPromoEligible?: boolean;
  memberDealsFirst?: boolean;
  plans: Plans;
  preselectedTier: PurchaseTier;
  promoCheckoutCampaign?: PromoCampaignQueryValue;
  promoImage?: PlansLandingPagePromoImage;
  subscriptionNew: SubscriptionsNewResponse;
  userId?: UserId;
};

// Plans page refresh. Designs:
// Desktop: https://www.figma.com/design/5lZfiEveGScwx75JKpB12W/Upsell-XPs-2026?node-id=5345-46758&m=dev
// Mobile: https://www.figma.com/design/5lZfiEveGScwx75JKpB12W/Upsell-XPs-2026?node-id=5399-25999&m=dev
export default function PlansLandingPage({
  className,
  coupon,
  headingOverride,
  isOsanoTopExperimentPage = false,
  isPeakPromoEligible,
  isPlansWelcomePlusInfoControlEnabled = false,
  isPlusPromoEligible,
  memberDealsFirst,
  plans,
  preselectedTier,
  promoCheckoutCampaign,
  promoImage,
  subscriptionNew,
  userId
}: PlansLandingPageProps) {
  /*
   * `tier` param selects the active/default tier
   * `showBothPlans` param controls whether one or both tiers are visible. Its default value depends on `tier`
   *
   *   /plans                                → Plus selected. Only plus visible
   *   /plans?tier=plus                      → Plus selected. Only plus visible
   *   /plans?showBothPlans=true             → Plus selected. Both plans visible
   *   /plans?tier=plus&showBothPlans=true   → Plus selected. Both plans visible
   *   /plans?tier=peak                      → Peak selected. Both plans visible
   *   /plans?tier=peak&showBothPlans=true   → Peak selected. Both plans visible
   *   /plans?tier=peak&showBothPlans=false  → Peak selected. Only peak visible
   *
   */
  const searchParams = useSearchParams();
  const showBothPlansParam = searchParams?.get('showBothPlans');
  const tierParam = searchParams?.get('tier');
  const [tierSelected, setTierSelected] = useState<PurchaseTier>(preselectedTier);

  const userSubscriptionTier = subscriptionNew.subscriptionTier;
  const [isUnableToUpgradeDialogOpen, setIsUnableToUpgradeDialogOpen] = useState(false);
  const [headToTheAppDialogState, setHeadToTheAppDialogState] = useState<{ isOpen: false } | { isOpen: true; provider: 'apple' | 'google' }>({
    isOpen: false
  });
  const isMobile = useIsMobileSizedScreen();
  const languageRegionCode = useLanguageRegionCode();
  const intl = useIntl();

  const [isNavigatingToCheckout, setIsNavigatingToCheckout] = useState(false);

  const sendOsanoTopExposure = useExposureEvent('web-growth-rm-osano-top');
  const sendPlansWelcomePlusInfoExposure = useExposureEvent('web-growth-rm-plans-welcome-plus-info');

  const ctaText = getPlansLandingPageCtaText({
    coupon,
    isPeakPromoEligible,
    isPlusPromoEligible,
    plans,
    subscriptionUser: subscriptionNew,
    tierSelected
  });

  const isPeakEligibleCoupon = coupon?.subscriptionTier === 'peak' || coupon?.subscriptionTier === 'any';
  const showBothPlansDefault = tierParam === 'peak' || isPeakEligibleCoupon;

  const showBothPlans = showBothPlansParam !== null ? showBothPlansParam === 'true' : showBothPlansDefault;

  const getAvailableTiers = (): 'peak-only' | 'plus-only' | 'plus-and-peak' => {
    if (userSubscriptionTier === 'plus') {
      return 'peak-only';
    }
    if (showBothPlans) {
      return 'plus-and-peak';
    }
    return tierParam === 'peak' ? 'peak-only' : 'plus-only';
  };
  const availableTiers = getAvailableTiers();
  const heading = headingOverride ?? getHeadingText({ plans, coupon, tierSelected });

  const planCheckoutUrl = getPlanCheckoutUrl({
    tierSelected,
    isAuthenticated: !!userId,
    languageRegionCode,
    searchParams,
    couponTier: coupon?.subscriptionTier,
    couponCode: coupon?.code,
    promoCheckoutCampaign
  });

  useEffect(() => {
    logProPurchasePageViewed({
      products_available: userSubscriptionTier === 'plus' ? 'peak' : 'plus-peak',
      purchase_screen_type_plan: getPurchaseScreenTypePlan(preselectedTier === 'peak' ? plans.peak : plans.plus, coupon)
    });
  }, [coupon, plans, preselectedTier, userSubscriptionTier]);

  useEffect(() => {
    if (isOsanoTopExperimentPage) {
      sendOsanoTopExposure();
    }
  }, [isOsanoTopExperimentPage, sendOsanoTopExposure]);

  /*
   * Only send the exposure event if the control is enabled via the /plans page.
   * The PlansLandingPage component is used across various pages and this value must be set to false in order to
   * avoid sending the exposure event to other pages until this experiment is fully rolled out.
   */
  useEffect(() => {
    if (isPlansWelcomePlusInfoControlEnabled) {
      sendPlansWelcomePlusInfoExposure();
    }
  }, [isPlansWelcomePlusInfoControlEnabled, sendPlansWelcomePlusInfoExposure]);

  const canCheckout =
    subscriptionNew.canPurchaseNewSubscription ||
    (subscriptionNew.canUpgradeExistingSubscription && subscriptionNew.subscriptionProvider === 'recurly');

  const plusSubheading = getPricingText({
    compactTrial: true,
    coupon,
    currencyCode: subscriptionNew.currencyCode,
    emphasizeAnnualCost: true,
    isMobileSizedScreen: isMobile,
    isPromoEligible: isPlusPromoEligible,
    plan: plans.plus,
    subscriptionUser: subscriptionNew
  });

  const peakSubheading = getPricingText({
    compactTrial: true,
    coupon: coupon?.subscriptionTier === 'peak' || coupon?.subscriptionTier === 'any' ? coupon : undefined,
    currencyCode: subscriptionNew.currencyCode,
    emphasizeAnnualCost: true,
    isMobileSizedScreen: isMobile,
    isPromoEligible: isPeakPromoEligible,
    plan: plans.peak,
    subscriptionUser: subscriptionNew
  });

  const planSelection = (
    <div className={styles.descriptionGroup}>
      {availableTiers === 'plus-only' && (
        <ContentBlock heading={heading} headingAs="h1" isPromoEligible={isPlusPromoEligible} subheading={plusSubheading} tier="plus" />
      )}
      {availableTiers === 'peak-only' && (
        <ContentBlock heading={heading} headingAs="h1" isPromoEligible={isPeakPromoEligible} subheading={peakSubheading} tier="peak" />
      )}
      {availableTiers === 'plus-and-peak' && (
        <div className={styles.headingGroup}>
          <Heading as="h1" className={styles.heading} size="400">
            {heading}
          </Heading>
        </div>
      )}
      {availableTiers === 'plus-only' && plans.plus.isTrial && (
        <TrialReminder cost={plans.plus.priceTotal} currencyCode={subscriptionNew.currencyCode} tier="plus" />
      )}
      {availableTiers === 'peak-only' && plans.peak.isTrial && (
        <TrialReminder cost={plans.peak.priceTotal} currencyCode={subscriptionNew.currencyCode} tier="peak" />
      )}
    </div>
  );

  const promoImageElement = promoImage && (
    <div
      className={classNames(styles.promoImageContainer, {
        [styles.promoImageContainerSingleTier]: availableTiers === 'peak-only' || availableTiers === 'plus-only'
      })}
    >
      <Image alt={promoImage.alt} height={promoImage.height} src={promoImage.src} width={promoImage.width} />
    </div>
  );

  return (
    <Page hideGoogleOneTap hideReturningUserUpsell hideAppleRedirectUpsell>
      <div className={classNames(styles.container, className)}>
        <Image
          priority
          src={backgroundSrc}
          alt={intl.formatMessage({
            defaultMessage:
              'People relaxing and playing by a mountain lake surrounded by tall conifer trees, with rocky peaks visible in the background. In the foreground, a person in a blue shirt skips stones into the calm water while others sit and rest on the rocky shoreline nearby.'
          })}
          className={styles.img}
          sizes={`(min-width: ${BREAKPOINT_SM_SIZE}px) 100vw, 0px`}
        />
        <div className={styles.contentContainer}>
          <div className={styles.leftColumn}>
            <div className={styles.descriptionContainer}>
              {promoImageElement}
              {planSelection}
            </div>
            {availableTiers === 'plus-and-peak' && (
              <PlanSelection
                className={styles.planSelectionContainer}
                compactPricing
                coupon={coupon}
                isPromoEligible={subscriptionNew.isPromoEligible}
                plans={plans}
                setTierSelected={setTierSelected}
                showPlusFirst={preselectedTier === 'plus'}
                tierSelected={tierSelected}
                trigger="Plan Page"
                subscriptionUser={subscriptionNew}
              />
            )}
            <div className={styles.ctaContainer}>
              <Button
                variant="accent"
                width="fullOnMobile"
                text={ctaText}
                loading={isNavigatingToCheckout}
                {...(canCheckout && { linkInfo: { href: planCheckoutUrl, prefetch: true } })}
                onClick={() => {
                  logProPurchaseGetStartedClicked({
                    product_type: tierSelected === 'peak' ? 'peak' : 'plus',
                    purchase_screen_type_plan: getPurchaseScreenTypePlan(tierSelected === 'peak' ? plans.peak : plans.plus, coupon),
                    trigger: 'Plan Page'
                  });

                  if (canCheckout) {
                    setIsNavigatingToCheckout(true);
                  } else if (
                    subscriptionNew.canUpgradeExistingSubscription &&
                    (subscriptionNew.subscriptionProvider === 'apple' || subscriptionNew.subscriptionProvider === 'google')
                  ) {
                    setHeadToTheAppDialogState({ isOpen: true, provider: subscriptionNew.subscriptionProvider });
                  } else {
                    setIsUnableToUpgradeDialogOpen(true);
                  }
                }}
              />
              <Typography variant="text100">
                <FormattedMessage defaultMessage="Cancel anytime in settings" />
              </Typography>
            </div>
          </div>
          <div className={styles.includesContainer}>
            {tierSelected === 'plus' ? (
              <PlusMembershipIncludes isInUs={subscriptionNew.countryCode === 'US'} memberDealsFirst={memberDealsFirst} />
            ) : (
              <PeakMembershipIncludes isInUs={subscriptionNew.countryCode === 'US'} />
            )}
          </div>
        </div>
      </div>
      {headToTheAppDialogState.isOpen && (
        <HeadToTheAppDialog provider={headToTheAppDialogState.provider} onCloseRequest={() => setHeadToTheAppDialogState({ isOpen: false })} />
      )}
      {isUnableToUpgradeDialogOpen && <UnableToUpgradeDialog onCloseRequest={() => setIsUnableToUpgradeDialogOpen(false)} />}
    </Page>
  );
}
